import { useEffect, useId } from "react";
import { completeSchemaGraph, type SchemaGraph, type SchemaNode } from "@/lib/schemaEntityGraph";

type SchemaInput = SchemaGraph | SchemaNode | string | null | undefined;

const GRAPH_ID = "integrated-social-schema-graph";
const runtimeEntries = new Map<string, SchemaInput>();
let activeRoute = "";
let loadedStaticGraph = false;

function currentRoute() {
  if (typeof window === "undefined") return "";
  const path = window.location.pathname || "/";
  return path === "/" ? "/" : `${path.replace(/\/+$/, "")}/`;
}

function parseSchema(input: SchemaInput): SchemaNode[] {
  if (!input) return [];
  try {
    const parsed = typeof input === "string" ? JSON.parse(input) : input;
    if (!parsed || typeof parsed !== "object") return [];
    const record = parsed as SchemaGraph & SchemaNode;
    return Array.isArray(record["@graph"]) ? record["@graph"] ?? [] : [record];
  } catch {
    return [];
  }
}

function getOrCreateGraphElement(route: string) {
  let script = document.getElementById(GRAPH_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = GRAPH_ID;
    script.type = "application/ld+json";
    script.dataset.siteSchema = "true";
    document.head.appendChild(script);
  }
  script.dataset.schemaRoute = route;
  return script;
}

function loadStaticGraph(route: string) {
  if (loadedStaticGraph) return;
  const script = document.getElementById(GRAPH_ID) as HTMLScriptElement | null;
  if (script?.dataset.schemaRoute === route && script.textContent) {
    runtimeEntries.set("__static_prerender__", script.textContent);
  }
  loadedStaticGraph = true;
}

function writeRuntimeGraph(route: string) {
  const nodes = Array.from(runtimeEntries.values()).flatMap(parseSchema);
  const script = getOrCreateGraphElement(route);
  script.textContent = JSON.stringify(
    completeSchemaGraph({ "@context": "https://schema.org", "@graph": nodes }, route),
  ).replace(/</g, "\\u003c");
}

function ensureRoute() {
  const route = currentRoute();
  if (route !== activeRoute) {
    activeRoute = route;
    runtimeEntries.clear();
    loadedStaticGraph = false;
    loadStaticGraph(route);
  }
  return route;
}

/** Clears the previous page graph before a non-local SPA route renders. */
export function prepareRuntimeSchemaRoute() {
  if (typeof document === "undefined") return;
  const route = currentRoute();
  if (route.startsWith("/local/")) return;
  if (route === activeRoute) return;

  activeRoute = route;
  runtimeEntries.clear();
  loadedStaticGraph = false;
  const script = document.getElementById(GRAPH_ID) as HTMLScriptElement | null;
  if (script?.dataset.schemaRoute !== route) script?.remove();
  loadStaticGraph(route);
}

/**
 * Adds one route-local schema contribution to the single client-side head graph.
 * The prerendered graph is retained as an initial source, then replaced atomically
 * when a visitor navigates between routes in the SPA.
 */
export function registerRuntimeSchema(source: string, schema: SchemaInput) {
  if (typeof document === "undefined") return () => undefined;
  const route = ensureRoute();
  runtimeEntries.set(source, schema);
  writeRuntimeGraph(route);

  return () => {
    if (currentRoute() !== route) return;
    runtimeEntries.delete(source);
    writeRuntimeGraph(route);
  };
}

function isPrerenderPass() {
  return Boolean((globalThis as typeof globalThis & { __INTEGRATED_SOCIAL_PRERENDER__?: boolean })
    .__INTEGRATED_SOCIAL_PRERENDER__);
}

/**
 * Declarative route schema contribution. During prerender it remains serializable
 * for extraction into the document head. In a browser it never renders JSON-LD in
 * the application body; it maintains one merged graph in <head> instead.
 */
export default function SchemaScript({ schema }: { schema: SchemaInput }) {
  const source = useId();
  const preserveLocalBodySchema = typeof window !== "undefined" && currentRoute().startsWith("/local/");

  useEffect(() => {
    if (preserveLocalBodySchema) return undefined;
    return registerRuntimeSchema(source, schema);
  }, [source, schema, preserveLocalBodySchema]);

  if (!isPrerenderPass() && !preserveLocalBodySchema) return null;
  const json = typeof schema === "string" ? schema : JSON.stringify(schema).replace(/</g, "\\u003c");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;
}
